// src/pages/Dashboard/hooks/useDashboardDocuments.js

import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { DEFAULT_LAWYER_CONFIG } from '../utils/dashboardConstants';

/**
 * Hook customizado para gerenciar geração de documentos
 * Encapsula a lógica de geração de documentos e download
 * 
 * @param {object} casoSelecionado - Caso atualmente selecionado
 * @returns {object} - Estado e funções para gerenciar documentos
 */
export const useDashboardDocuments = (casoSelecionado) => {
  const { user } = useAuth();

  const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
  const [instrucoesAdicionais, setInstrucoesAdicionais] = useState('');
  const [conteudoGerado, setConteudoGerado] = useState('');
  const [conteudoGeradoDisponivel, setConteudoGeradoDisponivel] = useState(false);
  const [gerandoCorpoDocumento, setGerandoCorpoDocumento] = useState(false);
  const [baixandoDocumento, setBaixandoDocumento] = useState(false);
  const [documentoGeradoId, setDocumentoGeradoId] = useState(null); // ✅ NOVO

  /**
   * Função auxiliar para parse seguro de JSON
   */
  const safeParse = useCallback((data) => {
    if (!data) return null;
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Erro ao fazer parse:', error);
      return null;
    }
  }, []);

  /**
   * Seleciona um documento para gerar
   * @param {object} documento - Documento selecionado
   */
  const selecionarDocumento = useCallback((documento) => {
    setDocumentoSelecionado(documento);
    setInstrucoesAdicionais('');
    setConteudoGerado('');
    setConteudoGeradoDisponivel(false);
    setDocumentoGeradoId(null);
    console.log('✅ Documento selecionado:', documento.titulo);
  }, []);

  /**
   * Limpa o documento selecionado e reseta estado
   */
  const limparDocumentoSelecionado = useCallback(() => {
    setDocumentoSelecionado(null);
    setInstrucoesAdicionais('');
    setConteudoGerado('');
    setConteudoGeradoDisponivel(false);
    setGerandoCorpoDocumento(false);
    setBaixandoDocumento(false);
    setDocumentoGeradoId(null);
  }, []);

  /**
   * Atualiza as instruções adicionais
   * @param {string} instrucoes - Novas instruções
   */
  const atualizarInstrucoesAdicionais = useCallback((instrucoes) => {
    setInstrucoesAdicionais(instrucoes);
  }, []);

  /**
   * Gera o corpo do documento usando IA e salva no banco
   * ✅ MODIFICADO: Agora salva o documento gerado via backend
   * @param {string} instrucoesParam - Instruções adicionais (opcional)
   * @param {string} nomeDocumentoParam - Nome do documento (opcional)
   * @returns {Promise<boolean>} - true se gerado com sucesso
   */
  const gerarCorpoDocumento = useCallback(async (instrucoesParam = null, nomeDocumentoParam = null) => {
    if (!documentoSelecionado && !nomeDocumentoParam) {
      throw new Error('Documento não selecionado');
    }
    if (!casoSelecionado) {
      throw new Error('Caso não selecionado');
    }

    setGerandoCorpoDocumento(true);
    try {
      const analiseCaso = safeParse(casoSelecionado.analise_caso);
      const dadosCliente = safeParse(casoSelecionado.dados_cliente);

      const config = {
        advogado: user.nome || '',
        oa: user.documento || '',
        domicilio: DEFAULT_LAWYER_CONFIG.domicilio
      };

      const instrucoesFinais = instrucoesParam !== null ? instrucoesParam : instrucoesAdicionais;
      const nomeDocumentoFinal = nomeDocumentoParam || documentoSelecionado?.titulo || '';

      const payload = {
        analise_caso: analiseCaso ? JSON.stringify(analiseCaso) : '',
        documento: nomeDocumentoFinal,
        instrucao: instrucoesFinais,
        dados_cliente: dadosCliente ? JSON.stringify(dadosCliente) : '',
        dados_advogado: JSON.stringify(config),
        id_law_case: casoSelecionado.id // ✅ NOVO: Enviar ID do caso
      };

      console.log('📄 Gerando documento:', nomeDocumentoFinal);
      console.log('📄 Caso ID:', casoSelecionado.id);

      // ✅ MODIFICADO: Chamar endpoint do backend que salva documento
      const response = await axios.post(
        '/api/documentos/gerar-dashboard',
        payload,
        {
          timeout: 120000
        }
      );

      if (response.data && response.data.success) {
        const conteudo = response.data.data.conteudo;
        const docId = response.data.data.documentoGeradoId;

        setConteudoGerado(conteudo);
        setConteudoGeradoDisponivel(true);
        setDocumentoGeradoId(docId);

        console.log('✅ Documento gerado e salvo com sucesso. ID:', docId);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Erro ao gerar documento:', error);
      
      let errorMessage = 'Erro ao gerar conteúdo do documento';
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'API de geração não encontrada';
        } else if (error.response.status === 401) {
          errorMessage = 'Erro de autenticação na API';
        } else if (error.response.status === 500) {
          errorMessage = 'Erro interno no servidor';
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Timeout na geração - tente novamente';
      }
      
      throw new Error(errorMessage);
    } finally {
      setGerandoCorpoDocumento(false);
    }
  }, [documentoSelecionado, casoSelecionado, instrucoesAdicionais, user, safeParse]);

  /**
   * Copia o conteúdo gerado para a área de transferência
   * @returns {Promise<boolean>} - true se copiado com sucesso
   */
  const copiarConteudo = useCallback(async () => {
    if (!conteudoGerado) return false;

    try {
      await navigator.clipboard.writeText(conteudoGerado);
      console.log('✅ Conteúdo copiado para área de transferência');
      return true;
    } catch (error) {
      console.error('❌ Erro ao copiar conteúdo:', error);
      throw new Error('Não foi possível copiar o conteúdo');
    }
  }, [conteudoGerado]);

  /**
   * Baixa o documento como arquivo DOCX
   * @param {string} nomeDocumentoParam - Nome customizado (opcional)
   * @returns {Promise<boolean>} - true se baixado com sucesso
   */
  const downloadDocx = useCallback(async (nomeDocumentoParam = null) => {
    if (!conteudoGerado) return false;

    setBaixandoDocumento(true);

    try {
      const nomeDocumentoFinal = nomeDocumentoParam || documentoSelecionado?.titulo || 'documento';
      
      const payload = {
        texto_conteudo: conteudoGerado,
        modelo_path: 'modelo_geral.docx',
        nome_arquivo_base: nomeDocumentoFinal.replace(/\s+/g, '_'),
        return_as: 'bytes'
      };

      console.log('📥 Gerando DOCX para download:', nomeDocumentoFinal);

      const response = await axios.post(
        'https://api-sniperspt.pythonanywhere.com/api/document/generate_from_text',
        payload,
        {
          headers: {
            'X-API-KEY': 'APIKEY##',
            'Content-Type': 'application/json'
          },
          responseType: 'blob',
          timeout: 60000
        }
      );

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${nomeDocumentoFinal.replace(/\s+/g, '_')}.docx`;
      if (contentDisposition && contentDisposition.includes('filename=')) {
        filename = contentDisposition
          .split('filename=')[1]
          .split(';')[0]
          .replace(/"/g, '');
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ Documento baixado com sucesso:', filename);
      return true;

    } catch (error) {
      console.error('❌ Erro ao gerar DOCX:', error);
      
      let errorMessage = 'Erro ao gerar arquivo DOCX';
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'API de conversão não encontrada';
        } else if (error.response.status === 401) {
          errorMessage = 'Erro de autenticação na API';
        } else if (error.response.status === 500) {
          errorMessage = 'Erro interno no servidor de conversão';
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Timeout na conversão - tente novamente';
      }
      
      throw new Error(errorMessage);
    } finally {
      setBaixandoDocumento(false);
    }
  }, [conteudoGerado, documentoSelecionado]);

  return {
    // Estado
    documentoSelecionado,
    instrucoesAdicionais,
    conteudoGerado,
    conteudoGeradoDisponivel,
    gerandoCorpoDocumento,
    baixandoDocumento,
    documentoGeradoId, // ✅ NOVO
    
    // Funções
    selecionarDocumento,
    limparDocumentoSelecionado,
    atualizarInstrucoesAdicionais,
    gerarCorpoDocumento,
    copiarConteudo,
    downloadDocx
  };
};